import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchConfidentialDocuments, fetchNotificationUnviewed, fetchRoom, fetchRoomNotifications } from 'store/reducers/roomsCreator';
import { IDataRoom, IDocument, INotification } from '../../interfaces';

interface DataState {
  dataRoom: IDataRoom | null;
  isLoadingRoom: boolean;
  errorRooms: string;

  confidentialDocuments: IDocument[];
  isLoadingConfidentialDocuments: boolean;
  errorLoadingConfidentialDocuments: string;

  isFolderCreateOpen: boolean;
  isFileCreateOpen: boolean;
  isEmptyFileCreateOpen: boolean;
  isLogsDetailsOpen: boolean;
  notifications: INotification[];
  notificationStatus: 'pending' | 'empty' | 'fulfilled';
  isLoadingNotifications: boolean;
  notificationPagination: { current: number; per_page: number; total: number; pages: number };
  notificationsUnviewed: number;
  roomToConfirm: IDataRoom | null;
  isOpenConfrimInvite: boolean;

  isOpenAttachRoomModal: boolean;
  currentRelatedRoomTerms: IDocument[];
  parentRoom: IDataRoom | null;
  needToUpdateTerms: boolean;
}

const initialState: DataState = {
  dataRoom: null,
  isLoadingRoom: false,
  errorRooms: '',

  confidentialDocuments: [],
  isLoadingConfidentialDocuments: false,
  errorLoadingConfidentialDocuments: '',

  isFolderCreateOpen: false,
  isFileCreateOpen: false,
  isEmptyFileCreateOpen: false,
  isLogsDetailsOpen: false,
  notifications: [],
  notificationStatus: 'pending',
  isLoadingNotifications: false,
  notificationPagination: { current: 1, per_page: 40, total: 0, pages: 1 },
  notificationsUnviewed: 0,
  roomToConfirm: null,
  isOpenConfrimInvite: false,

  isOpenAttachRoomModal: false,
  currentRelatedRoomTerms: [],
  parentRoom: null,
  needToUpdateTerms: false,
};

const dataRoomSlice = createSlice({
  name: 'dataRoom',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchRoom.fulfilled, (state, action: PayloadAction<IDataRoom>) => {
      state.isLoadingRoom = false;
      state.errorRooms = '';
      state.dataRoom = action.payload;
    });
    builder.addCase(fetchRoom.pending, (state) => {
      state.isLoadingRoom = true;
    });
    builder.addCase(fetchRoom.rejected, (state, action: any) => {
      state.isLoadingRoom = false;
      if (action.payload) {
        // Since we passed in `MyKnownError` to `rejectValue` in `updateUser`, the type information will be available here.
        state.errorRooms = action.payload.errorMessage;
      } else {
        state.errorRooms = action.error.message;
      }
    });
    builder.addCase(fetchConfidentialDocuments.fulfilled, (state, action: PayloadAction<IDocument[]>) => {
      state.confidentialDocuments = action.payload;
      state.isLoadingConfidentialDocuments = false;
      state.errorLoadingConfidentialDocuments = '';
    });
    builder.addCase(fetchConfidentialDocuments.pending, (state) => {
      state.isLoadingConfidentialDocuments = true;
    });
    builder.addCase(fetchConfidentialDocuments.rejected, (state, action: any) => {
      state.isLoadingConfidentialDocuments = false;
      if (action.payload) {
        // Since we passed in `MyKnownError` to `rejectValue` in `updateUser`, the type information will be available here.
        state.errorLoadingConfidentialDocuments = action.payload.errorMessage;
      } else {
        state.errorLoadingConfidentialDocuments = action.error.message;
      }
    });

    builder.addCase(fetchRoomNotifications.pending, (state, action) => {
      state.notificationStatus = 'pending';
      state.isLoadingNotifications = true;
    });

    builder.addCase(fetchRoomNotifications.fulfilled, (state, action) => {
      state.notifications = action.payload.root;
      state.notificationPagination = action.payload.pagination;
      state.isLoadingNotifications = false;
    });

    builder.addCase(fetchRoomNotifications.rejected, (state, action: any) => {
      state.isLoadingNotifications = false;
      if (action.message === 'canceled' || action.message === 'Aborted') return;
      state.notificationStatus = 'empty';
    });

    builder.addCase(fetchNotificationUnviewed.fulfilled, (state, action: PayloadAction<number>) => {
      state.isLoadingRoom = false;
      state.errorRooms = '';
      state.notificationsUnviewed = action.payload;
    });
  },
  reducers: {
    setDataRoom(state, action: PayloadAction<IDataRoom | null>) {
      state.dataRoom = action.payload;
    },
    setIsLoadingRoom(state, action: PayloadAction<boolean>) {
      state.isLoadingRoom = action.payload;
    },
    setIsFolderCreateOpen(state, action: PayloadAction<boolean>) {
      state.isFolderCreateOpen = action.payload;
    },
    setIsFileCreateOpen(state, action: PayloadAction<boolean>) {
      state.isFileCreateOpen = action.payload;
    },
    setIsEmptyFileCreateOpen(state, action: PayloadAction<boolean>) {
      state.isEmptyFileCreateOpen = action.payload;
    },
    setIsLogsDetailsOpen(state, action: PayloadAction<boolean>) {
      state.isLogsDetailsOpen = action.payload;
    },
    setNotifications(state, action: PayloadAction<INotification[]>) {
      state.notifications = action.payload;
    },
    setReplacedNotifications(state, action: PayloadAction<INotification[]>) {
      state.notifications = state.notifications.map((not) => action.payload.find(({ id }) => not.id === id) || not);
    },
    setNotificationsStatus(state, action: PayloadAction<'pending' | 'empty' | 'fulfilled'>) {
      state.notificationStatus = action.payload;
    },
    setIsLoadingNotifications(state, action: PayloadAction<boolean>) {
      state.isLoadingNotifications = action.payload;
    },
    setRoomToConfirm(state, action: PayloadAction<null | IDataRoom>) {
      state.roomToConfirm = action.payload;
    },
    setIsOpenConfrimInvite(state, action: PayloadAction<boolean>) {
      state.isOpenConfrimInvite = action.payload;
    },
    setUnviewedNotifications(state, action: PayloadAction<number>) {
      state.notificationsUnviewed = action.payload;
    },
    resetNotifications(state) {
      state.notifications = [];
      state.notificationStatus = 'pending';
      state.isLoadingNotifications = false;
      state.notificationPagination = { current: 1, per_page: 40, total: 0, pages: 1 };
      state.notificationsUnviewed = 0;
    },

    setIsOpenAttachRoomModal(state, action: PayloadAction<{isOpen: boolean, parentRoom: IDataRoom | null}>) {
      state.isOpenAttachRoomModal = action.payload.isOpen;
      state.parentRoom = action.payload.parentRoom;
    },
    setCurrentRelatedRoomTerms(state, action: PayloadAction<IDocument[]>) {
      state.currentRelatedRoomTerms = action.payload;
    },
    setNeedToUpdateTerms(state, action: PayloadAction<boolean>) {
      state.needToUpdateTerms = action.payload;
    },
  },
});

const dataRoomReducer = dataRoomSlice.reducer;

export const {
  setDataRoom,
  setIsLoadingRoom,
  setIsFolderCreateOpen,
  setIsFileCreateOpen,
  setIsEmptyFileCreateOpen,
  setIsLogsDetailsOpen,
  setNotifications,
  setNotificationsStatus,
  setIsLoadingNotifications,
  setReplacedNotifications,
  setRoomToConfirm,
  setIsOpenConfrimInvite,
  setUnviewedNotifications,
  resetNotifications,
  setIsOpenAttachRoomModal,
  setCurrentRelatedRoomTerms,
  setNeedToUpdateTerms,
} = dataRoomSlice.actions;
export default dataRoomReducer;
